import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import { createPortal } from 'react-dom'
import Icon from '../../components/Icon'
import { useNarrow } from '../../hooks/useNarrow'
import { Dialog, PHONE_SHEET_QUERY } from '../feedback/Dialog'

/**
 * Select — the EDIMI dropdown.
 *
 * WHY THIS EXISTS AT ALL
 * Domi looked at the order screen and said the dropdowns are "totalnie raw i wgl
 * nie w stylu edimi.ai". He was right, and the cause was not laziness: `Order.tsx`
 * put `className="field"` on a `<select>`, but `.field` styles `.field input` —
 * so the control inherited nothing and rendered as the operating system's own
 * widget, blue focus ring and system arrow, inside a carefully styled card.
 *
 * WHY NOT JUST `appearance: none` ON A NATIVE `<select>`
 * That fixes the CLOSED control and nothing else. The panel a user sees after
 * clicking is still the OS list — different font, different radius, different
 * highlight colour on every machine — and the panel is the part of a dropdown
 * people actually look at. So this is a real listbox: trigger button plus panel,
 * both ours.
 *
 * WHAT THAT COSTS, AND HOW IT IS PAID
 * A hand-rolled listbox is the classic way to lose keyboard and screen-reader
 * support, so this one implements the whole APG combobox contract rather than a
 * plausible-looking subset: focus stays on the trigger and the active option is
 * pointed at with `aria-activedescendant` (the pattern that does not fight the
 * screen reader's own cursor), Arrow/Home/End move, typeahead jumps, Escape
 * cancels, Enter commits, Tab commits and leaves, and a click outside closes.
 * Disabled options are skipped by every one of those movements, not merely
 * greyed.
 *
 * `name` renders a hidden input so the control still contributes a value to a
 * native form POST — `BillingForm` submits a real form, and a listbox that
 * silently drops its value there would be worse than the raw `<select>`.
 */

export interface SelectOption {
  value: string
  label: string
  /** Decorative content before the readable option name, e.g. a language flag. */
  leading?: ReactNode
  /** Druga kolumna pozycji, mono: czas trwania, liczba klipów (artboard 02). */
  meta?: string
  /**
   * Krój, którym składa się etykieta TEJ pozycji — na liście i na zamkniętym polu
   * (reguła 41 języka minimal: nazwa kroju bez próbki nic nie mówi). Wołający
   * odpowiada za to, żeby rodzina była załadowana.
   */
  fontFamily?: string
  disabled?: boolean
}

export interface SelectProps {
  value: string
  onChange: (value: string) => void
  options: SelectOption[]
  /** Label rendered above the control, wired to it with aria-labelledby. */
  label?: string
  /** Helper text below the control. */
  hint?: string
  /** Error text below the control; also marks the trigger aria-invalid. */
  error?: string
  /** Shown when `value` matches no option — an unmade choice stays visibly unmade. */
  placeholder?: string
  disabled?: boolean
  id?: string
  /** Name for the hidden input, so a native form submit carries the value. */
  name?: string
  className?: string
  'aria-label'?: string
  /**
   * Where the open list lives. `inline` (default) drops it under the trigger inside the
   * root, right for a form on a page. `viewport` pins it to the trigger's on-screen
   * rectangle with `position: fixed`, so a scroll container around the control — a dialog
   * body, a step sheet — cannot clip it (measured 2026-09-16: the caption pack list in the
   * planner's step dialog showed one option and a scrollbar). The list is measured on open,
   * flips above the trigger when the room below is smaller than the room above, and
   * follows the trigger on scroll and resize. It renders through a portal on <body>: a
   * `fixed` box inside a transformed ancestor is positioned against THAT box, and a dialog
   * keeps its entry `scale(.98)` for the length of its transition (measured: the list sat
   * 75 px low and stretched the dialog's scroll area until the transition ended).
   */
  panel?: 'inline' | 'viewport'
}

/** Viewport placement: gap under the trigger, margin to the screen edge, the cap ds.css sets
 *  on `.ds-select-panel`, and the floor below which the list flips rather than shrinks. */
const PANEL_GAP = 6
const PANEL_EDGE = 8
const PANEL_CAP = 280
const PANEL_MIN = 120
/** Below this width ds.css draws a dialog as a bottom sheet — a viewport list becomes one too:
 *  a floating list over a sheet either fell under the screen edge or covered the sheet's
 *  header and X (measured 2026-09-16/17 at 390×844). Jeden próg dla całego produktu:
 *  ten sam, którym `Dialog` przełącza okno w arkusz (reguła 49). */
const SHEET_QUERY = PHONE_SHEET_QUERY

export function Select({
  value,
  onChange,
  options,
  label,
  hint,
  error,
  placeholder,
  disabled,
  id,
  name,
  className = '',
  'aria-label': ariaLabel,
  panel = 'inline',
}: SelectProps) {
  const auto = useId()
  const rootId = id ?? `sel-${auto}`
  const listId = `${rootId}-list`
  const labelId = `${rootId}-label`
  const describedBy = [error ? `${rootId}-err` : null, hint ? `${rootId}-hint` : null]
    .filter(Boolean)
    .join(' ')

  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(-1)
  const [panelStyle, setPanelStyle] = useState<CSSProperties | undefined>(undefined)
  const [placement, setPlacement] = useState<'down' | 'up'>('down')
  const narrow = useNarrow(SHEET_QUERY)
  const sheet = panel === 'viewport' && narrow
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLUListElement>(null)
  // Typeahead buffer. Kept in a ref, not state: it must not re-render, and a
  // stale closure over it would break the second keystroke of "sw" -> Sweden.
  const typed = useRef({ buf: '', at: 0 })

  const selectedIndex = options.findIndex((o) => o.value === value)
  const selected = selectedIndex >= 0 ? options[selectedIndex] : undefined

  const firstEnabled = useCallback(
    (from: number, step: number) => {
      for (let i = 0; i < options.length; i++) {
        const idx = (from + step * i + options.length * options.length) % options.length
        if (!options[idx].disabled) return idx
      }
      return -1
    },
    [options],
  )

  const openPanel = useCallback(
    (start?: number) => {
      if (disabled) return
      setOpen(true)
      setActive(start ?? (selectedIndex >= 0 ? selectedIndex : firstEnabled(0, 1)))
    },
    [disabled, selectedIndex, firstEnabled],
  )

  const commit = useCallback(
    (idx: number) => {
      const option = options[idx]
      if (!option || option.disabled) return
      onChange(option.value)
      setOpen(false)
      triggerRef.current?.focus()
    },
    [options, onChange],
  )

  // Close on a click anywhere else. `pointerdown` rather than `click` so the
  // panel is gone before the other control receives its own event — closing on
  // `click` swallows the first tap on whatever the user actually aimed at.
  useEffect(() => {
    // A sheet has its own scrim and close; the outside-tap rule is for the floating list.
    if (!open || sheet) return
    const away = (e: PointerEvent) => {
      const target = e.target as Node
      // The viewport list is not a descendant of the root (portal), but it is not "away".
      if (!rootRef.current?.contains(target) && !panelRef.current?.contains(target)) setOpen(false)
    }
    document.addEventListener('pointerdown', away)
    return () => document.removeEventListener('pointerdown', away)
  }, [open, sheet])

  // Keep the active option in view when the list is long enough to scroll.
  useEffect(() => {
    if (!open || active < 0) return
    // Queried by data-idx rather than by id: an id built from useId() contains
    // characters a selector has to escape, and CSS.escape does not exist in every
    // environment this runs in (jsdom included). scrollIntoView is optional for
    // the same reason — jsdom does not implement it, and a missing scroll must
    // not throw inside a keyboard handler.
    const el = panelRef.current?.querySelector<HTMLElement>(`[data-idx="${active}"]`)
    el?.scrollIntoView?.({ block: 'nearest' })
  }, [open, active, rootId])

  // `panel="viewport"`: pin the list to the trigger's on-screen rectangle. A layout effect,
  // so the first paint is already in place; the content height is `scrollHeight`, not the
  // rect — the rect would measure the cap this effect is about to set. A fixed box does not
  // follow the page, so every scroll and resize re-places it (closing on scroll would also
  // close on the keyboard's own scrollIntoView).
  useLayoutEffect(() => {
    if (!open || panel !== 'viewport' || sheet) {
      setPanelStyle(undefined)
      setPlacement('down')
      return
    }
    const place = () => {
      const trigger = triggerRef.current
      const list = panelRef.current
      if (!trigger || !list) return
      const rect = trigger.getBoundingClientRect()
      const below = window.innerHeight - rect.bottom - PANEL_GAP - PANEL_EDGE
      const above = rect.top - PANEL_GAP - PANEL_EDGE
      const need = Math.min(list.scrollHeight, PANEL_CAP)
      const up = below < need && above > below
      const maxHeight = Math.max(PANEL_MIN, Math.min(PANEL_CAP, up ? above : below))
      setPlacement(up ? 'up' : 'down')
      setPanelStyle({
        position: 'fixed',
        left: rect.left,
        width: rect.width,
        right: 'auto',
        maxHeight,
        ...(up
          ? { top: 'auto', bottom: window.innerHeight - rect.top + PANEL_GAP }
          : { top: rect.bottom + PANEL_GAP, bottom: 'auto' }),
      })
    }
    place()
    const onScroll = (e: Event) => {
      // The list's own scrolling moves nothing outside it.
      if (e.target instanceof Node && panelRef.current?.contains(e.target)) return
      place()
    }
    window.addEventListener('resize', place)
    document.addEventListener('scroll', onScroll, true)
    return () => {
      window.removeEventListener('resize', place)
      document.removeEventListener('scroll', onScroll, true)
    }
  }, [open, panel, sheet])

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return
    const key = e.key

    if (!open) {
      if (key === 'ArrowDown' || key === 'ArrowUp' || key === 'Enter' || key === ' ') {
        e.preventDefault()
        openPanel(key === 'ArrowUp' ? firstEnabled(options.length - 1, -1) : undefined)
      }
      return
    }

    switch (key) {
      case 'Escape':
        // Esc otwartej listy zamyka TYLKO listę — nie wędruje do warstwy, w której lista stoi.
        e.preventDefault()
        e.stopPropagation()
        setOpen(false)
        triggerRef.current?.focus()
        return
      case 'Enter':
      case ' ':
        e.preventDefault()
        commit(active)
        return
      case 'ArrowDown':
        e.preventDefault()
        setActive(firstEnabled(active + 1, 1))
        return
      case 'ArrowUp':
        e.preventDefault()
        setActive(firstEnabled(active - 1, -1))
        return
      case 'Home':
        e.preventDefault()
        setActive(firstEnabled(0, 1))
        return
      case 'End':
        e.preventDefault()
        setActive(firstEnabled(options.length - 1, -1))
        return
      case 'Tab':
        // Tab commits and leaves, like a native select. Not preventDefault'd:
        // the point is that focus moves on.
        setOpen(false)
        return
      default:
        break
    }

    // Typeahead. One-second window, so "po" finds Poland but a pause starts over.
    if (key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
      const now = e.timeStamp
      typed.current.buf = now - typed.current.at > 1000 ? key : typed.current.buf + key
      typed.current.at = now
      const needle = typed.current.buf.toLowerCase()
      const hit = options.findIndex((o) => !o.disabled && o.label.toLowerCase().startsWith(needle))
      if (hit >= 0) setActive(hit)
    }
  }

  const mountList = (list: ReactNode) => {
    if (panel !== 'viewport' || typeof document === 'undefined') return list
    if (!sheet) return createPortal(list, document.body)
    const heading = label ?? ariaLabel
    return createPortal(
      <Dialog
        onClose={() => { setOpen(false); triggerRef.current?.focus() }}
        title={heading}
        label={heading}
        className="ds-select-sheet"
      >
        {/* Arkusz BEZ AKCJI (reguła 49): wybór pozycji zamyka listę, więc na dole
            nie ma nic; nagłówek z X jest ten sam, co w każdym innym arkuszu. */}
        <div className="ds-sheet-body">{list}</div>
      </Dialog>,
      document.body,
    )
  }

  const cls = [
    'ds-select',
    open && 'is-open',
    disabled && 'is-disabled',
    error && 'is-error',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={cls} ref={rootRef}>
      {label ? (
        <span className="ds-select-label" id={labelId}>
          {label}
        </span>
      ) : null}

      <button
        type="button"
        ref={triggerRef}
        id={rootId}
        className="ds-select-trigger"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-haspopup="listbox"
        aria-labelledby={label ? `${labelId} ${rootId}` : undefined}
        aria-label={ariaLabel}
        aria-activedescendant={open && active >= 0 ? `${rootId}-o${active}` : undefined}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy || undefined}
        disabled={disabled}
        onClick={() => (open ? setOpen(false) : openPanel())}
        onKeyDown={onKeyDown}
      >
        <span
          className={selected ? 'ds-select-value' : 'ds-select-value is-placeholder'}
          style={selected?.fontFamily ? { fontFamily: selected.fontFamily } : undefined}
        >
          {selected?.leading}
          {selected ? selected.label : placeholder ?? ''}
        </span>
        <Icon name="ChevronDown" size={16} strokeWidth={2} className="ds-select-arrow" />
      </button>

      {open ? mountList(
        <ul
          className={[
            'ds-select-panel',
            panel === 'viewport' && !sheet && 'is-viewport',
            sheet && 'is-sheet',
            placement === 'up' && 'is-up',
          ].filter(Boolean).join(' ')}
          id={listId}
          role="listbox"
          ref={panelRef}
          // In the sheet the list itself holds focus (the trigger is under the scrim), so
          // the same keyboard contract runs on it and the sheet opens on the list.
          tabIndex={sheet ? 0 : -1}
          data-autofocus={sheet || undefined}
          onKeyDown={sheet ? onKeyDown : undefined}
          aria-labelledby={sheet && label ? labelId : undefined}
          aria-activedescendant={sheet && active >= 0 ? `${rootId}-o${active}` : undefined}
          style={sheet ? undefined : panelStyle}
          data-placement={panel === 'viewport' && !sheet ? placement : undefined}
        >
          {options.map((o, i) => (
            <li
              key={o.value}
              id={`${rootId}-o${i}`}
              data-idx={i}
              data-value={o.value}
              role="option"
              aria-selected={o.value === value}
              aria-disabled={o.disabled || undefined}
              className={[
                'ds-select-option',
                i === active && 'is-active',
                o.value === value && 'is-selected',
                o.disabled && 'is-disabled',
              ]
                .filter(Boolean)
                .join(' ')}
              // `pointerdown` fires before the trigger's blur, so the click is
              // not lost to the outside-close handler. In the sheet the list itself holds
              // focus and there is no outside-close, so the pick waits for `click`: a finger
              // that lands on a row to SCROLL the sheet must not pick that row (review
              // 2026-09-17), and a scroll gesture never produces a click.
              onPointerDown={sheet ? undefined : (e) => {
                e.preventDefault()
                commit(i)
              }}
              onClick={sheet ? () => commit(i) : undefined}
              onPointerEnter={() => !o.disabled && setActive(i)}
            >
              <span style={o.fontFamily ? { fontFamily: o.fontFamily } : undefined}>{o.leading}{o.label}</span>
              {o.meta ? <span className="ds-select-meta">{o.meta}</span> : null}
              {o.value === value ? <Icon name="Check" size={15} strokeWidth={2.4} /> : null}
            </li>
          ))}
        </ul>,
      ) : null}

      {name ? <input type="hidden" name={name} value={value} /> : null}
      {error ? (
        <div className="ds-select-error" id={`${rootId}-err`} role="alert">
          {error}
        </div>
      ) : null}
      {hint ? (
        <div className="ds-select-hint" id={`${rootId}-hint`}>
          {hint}
        </div>
      ) : null}
    </div>
  )
}

export default Select
